import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, Database, Globe, Cloud, AlertCircle, Copy, CheckCircle2, Upload, Image as ImageIcon } from 'lucide-react';
import { api, Plan, ApiError, Client } from '@/lib/api';

export default function ClientNewPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = React.useState(true);
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [successData, setSuccessData] = React.useState<Client | null>(null);
  const [generatedPassword, setGeneratedPassword] = React.useState<string | null>(null);
  const [generatedAdminEmail, setGeneratedAdminEmail] = React.useState<string | null>(null);
  const [copiedPassword, setCopiedPassword] = React.useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
  const [logoPreview, setLogoPreview] = React.useState<string | null>(null);
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const formFileInputRef = React.useRef<HTMLInputElement>(null);
  const [formData, setFormData] = React.useState({
    name: '',
    platformName: '',
    slug: '',
    planId: '',
    adminName: '',
    adminEmail: '',
    sendWelcomeEmail: true,
  });

  React.useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const data = await api.getPlans();
        setPlans(data);
        // Set default plan to first one if available
        if (data.length > 0) {
          setFormData((prev) => ({ ...prev, planId: data[0].id }));
        }
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.message || 'Erro ao carregar planos');
      } finally {
        setIsLoadingPlans(false);
      }
    };

    loadPlans();
  }, []);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handlePlatformNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const platformName = e.target.value;
    setFormData((prev) => ({
      ...prev,
      platformName,
      slug: generateSlug(platformName),
    }));
  };

  const formatPrice = (price: number, billingCycle: string) => {
    if (price === 0) return 'Sob Consulta';
    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
    return `${formatted}/${billingCycle === 'monthly' ? 'mês' : 'ano'}`;
  };

  const copyPassword = async () => {
    if (generatedPassword) {
      await navigator.clipboard.writeText(generatedPassword);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!successData) return;

    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de arquivo inválido. Apenas imagens são permitidas (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload
    try {
      setIsUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/tenants/${successData.slug}/logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao fazer upload');
      }

      const data = await response.json();
      setSuccessData(data);
      alert('Logo atualizado com sucesso!');
    } catch (err: any) {
      alert(err.message || 'Erro ao fazer upload do logo');
      setLogoPreview(null);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleFormLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo de arquivo inválido. Apenas imagens são permitidas (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    setLogoFile(file);
  };

  const uploadLogoToClient = async (clientId: string) => {
    if (!logoFile) {
      console.log('[Upload] No logo file selected');
      return;
    }

    const uploadUrl = `${import.meta.env.VITE_API_URL}/clients/${clientId}/logo`;

    console.log('[Upload] Uploading logo for client:', clientId);
    console.log('[Upload] Full URL:', uploadUrl);
    console.log('[Upload] Token present:', !!localStorage.getItem('accessToken'));

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      console.log('[Upload] Response status:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('[Upload] Logo uploaded successfully:', data);
        return data;
      } else {
        const text = await response.text();
        console.error('[Upload] Failed to upload logo. Status:', response.status);
        console.error('[Upload] Response text:', text);
        try {
          const error = JSON.parse(text);
          console.error('[Upload] Error details:', error);
        } catch {
          console.error('[Upload] Response was not JSON');
        }
      }
    } catch (err) {
      console.error('[Upload] Error uploading logo:', err);
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.createClient({
        name: formData.name,
        status: 'active',
        platform: {
          platformName: formData.platformName,
          slug: formData.slug,
          planId: formData.planId,
        },
        admin: formData.adminEmail ? {
          name: formData.adminName,
          email: formData.adminEmail,
        } : undefined,
      });

      // If logo was selected, upload it to the client
      if (logoFile && response.id) {
        console.log('[ClientNew] Uploading logo to client:', response.id);
        await uploadLogoToClient(response.id);
        console.log('[ClientNew] Logo upload completed');
      }

      // Extract generated password from response if available
      const clientResponse = response as any;
      if (clientResponse.generatedPassword && clientResponse.adminEmail) {
        setGeneratedPassword(clientResponse.generatedPassword);
        setGeneratedAdminEmail(clientResponse.adminEmail);
        setSuccessData(response);
      } else {
        // No password generated, go directly to list
        navigate('/clients');
      }
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao criar cliente');
      setIsLoading(false);
    }
  };

  // Success Screen - Show generated password
  if (successData) {
    return (
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Cliente Criado com Sucesso!</h1>
          <p className="text-gray-500 mt-2">
            O cliente <strong>{successData.name}</strong> foi criado e sua infraestrutura foi provisionada.
          </p>
        </div>

        {generatedPassword && generatedAdminEmail && (
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Credenciais do Administrador</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Anote ou copie a senha abaixo. Por seguranca, ela nao sera exibida novamente.
                </p>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between bg-white rounded-xl border border-amber-200 p-3">
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{generatedAdminEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-xl border border-amber-200 p-3">
                    <div>
                      <p className="text-xs text-gray-500">Senha</p>
                      <p className="font-mono font-medium text-gray-900">{generatedPassword}</p>
                    </div>
                    <button
                      onClick={copyPassword}
                      className="p-2 rounded-lg hover:bg-amber-100 transition-colors"
                    >
                      {copiedPassword ? (
                        <Check className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <Copy className="h-5 w-5 text-amber-600" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Informacoes do Cliente</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-xs text-gray-500">Nome da Empresa</p>
              <p className="font-medium text-gray-900">{successData.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <p className="font-medium text-emerald-600">Ativo</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Plataformas Criadas</p>
              <p className="font-medium text-gray-900">1 plataforma</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Data de Criacao</p>
              <p className="font-medium text-gray-900">{new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4">
          <Link
            to="/clients"
            className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Voltar para Clientes
          </Link>
          <Link
            to={`/clients/${successData.id}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25"
          >
            Ver Detalhes do Cliente
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/clients"
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Novo Cliente</h1>
          <p className="text-gray-500 mt-1">
            Crie uma nova empresa cliente e configure sua primeira plataforma de comunicacao
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 rounded-2xl border border-red-100 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Client Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-violet-50">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center">
                <span className="text-white font-bold text-lg">1</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cliente</h2>
                <p className="text-sm text-gray-500 mt-0.5">Informacoes da empresa que tera acesso a plataforma</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome da Empresa *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: ACME Corporation, Suvinil, Aspil..."
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
                <p className="text-xs text-gray-400">
                  Nome da empresa cliente (ex: Suvinil, Aspil, ACME Corp)
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Logo (Opcional)</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative h-20 w-20 rounded-xl border-2 border-gray-200 overflow-hidden bg-white">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-full w-full object-contain p-2"
                      />
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      ref={formFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFormLogoSelect}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => formFileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      {logoPreview ? 'Alterar Logo' : 'Selecionar Logo'}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      JPEG, PNG, GIF ou WEBP (máx. 5MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Information */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">2</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Plataforma</h2>
                <p className="text-sm text-gray-500 mt-0.5">Configure a plataforma de comunicacao do cliente</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome da Plataforma *</label>
                <input
                  type="text"
                  required
                  value={formData.platformName}
                  onChange={handlePlatformNameChange}
                  placeholder="Ex: Portal Suvinil, Comunidade Aspil..."
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
                <p className="text-xs text-gray-400">
                  Nome que aparecera na plataforma (gera o subdominio automaticamente)
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Subdominio *</label>
                <div className="flex">
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                    placeholder="portal-suvinil"
                    className="flex-1 rounded-l-xl border border-r-0 border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                  />
                  <span className="flex items-center rounded-r-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500 font-medium">
                    .nozcomunidades.com.br
                  </span>
                </div>
                <p className="text-xs text-gray-400">
                  URL de acesso: <span className="font-medium text-gray-600">{formData.slug || 'subdominio'}.nozcomunidades.com.br</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">3</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Plano da Plataforma</h2>
                <p className="text-sm text-gray-500 mt-0.5">Selecione o plano de assinatura para a plataforma</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {isLoadingPlans ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Nenhum plano disponivel</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, planId: plan.id }))}
                    className={`relative rounded-2xl border-2 p-5 text-left transition-all hover:shadow-md ${
                      formData.planId === plan.id
                        ? 'border-[#0057FF] bg-[#0057FF]/5 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {formData.planId === plan.id && (
                      <div className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#0057FF] flex items-center justify-center shadow-lg">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                    <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                    <p className="text-xl font-bold text-[#0057FF] mt-1">{formatPrice(plan.price, plan.billingCycle)}</p>
                    {plan.description && (
                      <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
                    )}
                    {plan.limits && Object.keys(plan.limits).length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Ate {plan.limits.maxUsers || 'ilimitados'} usuarios
                      </p>
                    )}
                    {plan.features && Object.keys(plan.features).length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {Object.entries(plan.features)
                          .filter(([, enabled]) => enabled)
                          .slice(0, 4)
                          .map(([feature]) => (
                            <li key={feature} className="text-xs text-gray-500 flex items-center gap-2">
                              <Check className="h-3.5 w-3.5 text-emerald-500" />
                              {feature.replace(/([A-Z])/g, ' $1').trim()}
                            </li>
                          ))}
                      </ul>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Admin User */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">4</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Administrador da Plataforma</h2>
                <p className="text-sm text-gray-500 mt-0.5">Usuario que tera acesso ao painel administrativo (opcional)</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nome do Administrador</label>
                <input
                  type="text"
                  value={formData.adminName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, adminName: e.target.value }))}
                  placeholder="Ex: Joao Silva"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Email do Administrador</label>
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData((prev) => ({ ...prev, adminEmail: e.target.value }))}
                  placeholder="admin@empresa.com"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF] transition-all"
                />
              </div>
            </div>
            {formData.adminEmail && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, sendWelcomeEmail: !prev.sendWelcomeEmail }))}
                  className={`h-6 w-11 rounded-full transition-colors ${
                    formData.sendWelcomeEmail ? 'bg-[#0057FF]' : 'bg-gray-200'
                  }`}
                >
                  <div
                    className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                      formData.sendWelcomeEmail ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <label className="text-sm text-gray-600">
                  Enviar email de boas-vindas com credenciais de acesso
                </label>
              </div>
            )}
            <p className="text-xs text-gray-400">
              Uma senha sera gerada automaticamente. Voce podera visualiza-la apos a criacao do cliente.
            </p>
          </div>
        </div>

        {/* Infrastructure Preview */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-gray-600 to-slate-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">5</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Infraestrutura Provisionada</h2>
                <p className="text-sm text-gray-500 mt-0.5">Recursos que serao criados automaticamente para a plataforma</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Database className="h-5 w-5 text-blue-500" />
                  </div>
                  <span className="font-medium text-gray-900">PostgreSQL Schema</span>
                </div>
                <code className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-lg border border-gray-200 inline-block">
                  tenant_{formData.slug || 'slug'}
                </code>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-emerald-500" />
                  </div>
                  <span className="font-medium text-gray-900">Subdominio</span>
                </div>
                <code className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-lg border border-gray-200 inline-block">
                  {formData.slug || 'slug'}.nozcomunidades.com.br
                </code>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Cloud className="h-5 w-5 text-amber-500" />
                  </div>
                  <span className="font-medium text-gray-900">Cloudflare R2 Bucket</span>
                </div>
                <code className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-lg border border-gray-200 inline-block">
                  noz-storage-{formData.slug || 'slug'}
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/clients"
            className="px-6 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isLoading || isLoadingPlans || !formData.planId}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0057FF] to-[#7C3AED] text-white font-medium text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#0057FF]/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Criando...
              </>
            ) : (
              'Criar Cliente'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
